import { useState, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  BookOpen, Search, ChevronRight, LogIn, LayoutDashboard, Users, CheckSquare,
  Repeat, Receipt, FileText, Bell, Settings as SettingsIcon,
  KeyRound, Filter, Trash2, Calendar as CalendarIcon, Kanban, ListChecks,
  Download, Printer,
} from "lucide-react";
import { cn } from "@/lib/utils";

const SECTIONS = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: LogIn,
    intro: "How to sign in, orient yourself, and use the workspace shell.",
    steps: [
      { img: "01-login.jpg", title: "Sign in", body: "Open the app URL, enter your email + password, and click Sign in. If your workspace has demo accounts enabled, one-click login buttons are shown at the bottom of the form. Forgot password? Use the link below Sign in." },
      { img: "02-dashboard.jpg", title: "The dashboard", body: "After sign-in you land on the dashboard — 6 KPI cards summarise today's tasks, overdue items, open tasks, pending documents, active clients and total documents. Below you'll see Today / Overdue / Upcoming lists and a live activity feed." },
    ],
  },
  {
    id: "clients",
    title: "Clients",
    icon: Users,
    intro: "Everything about creating, organising, and managing your clients — including outstanding balances and portal credentials.",
    steps: [
      { img: "03-clients-list.jpg", title: "The client list", body: "The Clients page lists every client in your workspace with Code, Name, Constitution, PAN, GSTIN, Mobile, Services, Outstanding balance, and a payment-reminder button. Use the search box (top-left) to filter by name/PAN/GSTIN. Click any row to open the client's detail view." },
      { img: "04-new-client.jpg", title: "Create a client", body: "Click 'New client' (top-right). Enter Client code, choose Constitution (Individual / Proprietorship / Partnership / LLP / Private Limited / Public Limited / HUF / Trust / Society / Other), Legal name, PAN, GSTIN, Email, Mobile, Phone, City, and toggle Services availed (GST, Income Tax, ROC, TDS, Tax Audit, Statutory Audit, Advisory, Payroll, Others). Click 'Create client'." },
      { img: "05-client-detail.jpg", title: "Client detail overview", body: "The Overview tab shows key info cards (PAN, GSTIN, Email, Mobile, Outstanding), lets you add internal notes, and lists everything else about the client via tabs: Credentials, Tasks, Documents, Timeline. If the client has outstanding fees + an email on file, a 'Send payment reminder' button appears in the top-right actions bar." },
      { img: "06-client-credentials.jpg", title: "Portal credentials", body: "The Credentials tab stores the login details you need to file returns on behalf of the client — Income Tax (PAN + password), GST (username + password), ROC/MCA (only for LLP/Private/Public Ltd), and TRACES (TAN + username + password). Fields are masked by default. Click the eye icon to reveal, the copy icon to copy to clipboard. Manager+ can edit; Article Assistants can view but not save." },
    ],
    tips: [
      "Bulk-archive clients: as a Partner, tick multiple rows → the floating BulkActionBar shows an 'Archive' action.",
      "The Outstanding column is calculated live from unpaid invoices — a Partner or Manager can click the gold Bell icon to send a payment-reminder email.",
    ],
  },
  {
    id: "tasks",
    title: "Tasks",
    icon: CheckSquare,
    intro: "The heart of your practice — track every filing, review, and assignment. Three views (Kanban, List, Calendar) plus category filtering and bulk ops.",
    steps: [
      { img: "07-tasks-kanban.jpg", title: "Kanban view", body: "The default Kanban view groups tasks by status: To do, In progress, Review, Done. Drag a card between columns to move it — the change syncs immediately. Coloured pill bar at the top shows pending-task counts by category (GST, Income Tax, ROC, Tax Audit, Statutory Audit, Others); click a pill to filter." },
      { img: "08-tasks-calendar.jpg", title: "Calendar view", body: "Switch to the Calendar tab for month/week/day views. Every task with a due date shows as a coloured event — colour = priority, strike-through = done. Click any event to open the task detail dialog." },
      { img: "09-tasks-list.jpg", title: "List view + bulk actions", body: "List view shows a dense table. Tick the checkbox column to select rows — a sticky BulkActionBar appears at the bottom with 'Mark done', 'In progress', 'Assign to me', and 'Delete' actions. Bulk delete requires an audit reason (min 5 characters)." },
      { img: "10-new-task.jpg", title: "Create a task", body: "Click 'New task'. Enter Title, Description (optional), Client, Assignee, Priority (Low/Medium/High/Urgent), Status (To do / In progress / Review / Done), Category (GST / Income Tax / ROC / Tax Audit / Statutory Audit / Others), and Due date. Click 'Create task'. Assigning the task to another user sends them an email + real-time notification." },
    ],
    tips: [
      "Filters at the top of Tasks — Status, Priority, 'Only mine' — combine with the category pill bar for pinpoint filtering.",
      "Clicking a task row opens a detail dialog where you can add comments, change assignee, update status, or delete (with audit reason).",
    ],
  },
  {
    id: "recurring",
    title: "Recurring Tasks",
    icon: Repeat,
    intro: "Automate repetitive filings — monthly GSTR-3B, quarterly TDS, annual ITR — and turn completed instances straight into invoices.",
    steps: [
      { img: "11-recurring.jpg", title: "Recurring rules", body: "Each row is a rule that auto-generates a task on schedule. Toggle the switch to enable/disable, click Play to run once immediately, or click the gold Receipt icon to bundle all completed instances of this rule into a single draft invoice. You can set an optional 'Default bill rate' per rule that pre-fills the invoice line rate." },
    ],
    tips: [
      "The engine ticks every 60 seconds. If a rule is due, a new task is generated with the linked assignee and due-date offset.",
      "Auto-invoice: when the completed tasks tied to a rule are ready, one click creates a GST-compliant draft invoice — one line per task.",
    ],
  },
  {
    id: "invoices",
    title: "Invoices",
    icon: Receipt,
    intro: "Generate GST-compliant invoices, track payments, and export PDFs.",
    steps: [
      { img: "12-invoices.jpg", title: "Invoicing", body: "Click 'New invoice' to raise one manually — pick a client, add line items with HSN/SAC codes, quantity, unit price, and GST rate. CGST/SGST/IGST is split automatically based on your workspace state vs the client's GSTIN state. Invoice numbers use financial-year-scoped prefixes (e.g. 2026-27/0001). Click any invoice to view detail, mark payments, download PDF, or send." },
    ],
    tips: [
      "Invoices auto-generated from a recurring rule land in 'Draft' status so you can review before issuing.",
      "The Outstanding column on the Clients page + the Send Reminder button both work off the ledger of unpaid invoices.",
    ],
  },
  {
    id: "documents",
    title: "Documents",
    icon: FileText,
    intro: "Upload PDFs, images, and any client file. Track pending documents. Version-control every replacement.",
    steps: [
      { img: "13-documents.jpg", title: "Document library", body: "Drop files onto the page or click Upload. Optionally tag a document to a client. Toggle 'Pending only' to see documents you're expecting from clients but haven't received. Tick rows to select — the BulkActionBar shows 'Mark received' (visible only when at least one selected doc is pending) and 'Delete' (audit-reason required)." },
    ],
    tips: [
      "Every upload creates a versioned entry. Uploading again against the same document creates version 2, 3, and so on — with restore-from-version support.",
    ],
  },
  {
    id: "notifications",
    title: "Notifications",
    icon: Bell,
    intro: "Assignments, comments, and reminders arrive live via WebSocket + email.",
    steps: [
      { img: "14-notifications.jpg", title: "Notification centre", body: "Every task assignment, comment mention, and due-date reminder shows up here in real time. An amber dot on the bell icon indicates unread items. Click a row to jump to the related item — or 'Mark all read' in the top-right. The pulsing green dot next to 'Live' means your WebSocket is connected." },
    ],
    tips: [
      "Task-assignment notifications are also emailed to the assignee over SMTP — configure SMTP_HOST / SMTP_USER / SMTP_PASSWORD in the backend .env.",
    ],
  },
  {
    id: "settings",
    title: "Settings & Team",
    icon: SettingsIcon,
    intro: "Manage your workspace, users, roles, and personal preferences.",
    steps: [
      { img: "16-settings.jpg", title: "Settings", body: "Change your password, view login history, toggle dark mode, and (as a Partner+) manage your firm's organisation profile — name, GSTIN, state, invoice numbering prefix, financial-year start month. The Team page (Partner+ only) is where you invite new users and assign roles: Super Admin, Partner, Manager, or Article Assistant." },
    ],
    tips: [
      "Roles control every sensitive action — bulk-archive clients, delete tasks, save credentials, send reminders. Check the role badge on each user in the Team page.",
    ],
  },
];

export default function HelpPage() {
  const [q, setQ] = useState("");
  const [activeId, setActiveId] = useState(SECTIONS[0].id);

  const filtered = useMemo(() => {
    if (!q.trim()) return SECTIONS;
    const needle = q.toLowerCase();
    return SECTIONS
      .map((s) => ({
        ...s,
        steps: s.steps.filter(
          (st) => st.title.toLowerCase().includes(needle) || st.body.toLowerCase().includes(needle),
        ),
      }))
      .filter((s) => s.steps.length > 0 || s.title.toLowerCase().includes(needle));
  }, [q]);

  const jumpTo = (id) => {
    setActiveId(id);
    document.getElementById(`sec-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="grid grid-cols-12 gap-6" data-testid="help-page">
      {/* Sidebar TOC */}
      <aside className="col-span-3 lg:col-span-3 xl:col-span-2 space-y-1 sticky top-4 self-start">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen size={16} className="text-primary" />
          <div className="font-heading font-semibold text-sm">User Manual</div>
        </div>
        <div className="relative mb-3">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search manual…" className="pl-8 h-8 text-xs" data-testid="help-search" />
        </div>
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => jumpTo(s.id)}
            className={cn(
              "w-full text-left px-2.5 py-1.5 rounded-md text-xs flex items-center gap-2 transition-colors",
              activeId === s.id ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
            )}
            data-testid={`help-toc-${s.id}`}
          >
            <s.icon size={12} />
            <span className="truncate">{s.title}</span>
            <ChevronRight size={12} className="ml-auto opacity-40" />
          </button>
        ))}
        <div className="pt-4 space-y-1.5 border-t border-border mt-4">
          <a
            href="/USER_MANUAL.md"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 text-xs text-primary hover:underline px-2.5 py-1.5"
            data-testid="help-download-md"
          >
            <Download size={12} /> Download Markdown
          </a>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 text-xs text-primary hover:underline px-2.5 py-1.5 w-full"
            data-testid="help-print"
          >
            <Printer size={12} /> Print manual
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="col-span-9 lg:col-span-9 xl:col-span-10 space-y-10 print:col-span-12" data-testid="help-content">
        <header className="border-b border-border pb-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">CA OS · Version 1.7</div>
          <h1 className="font-heading font-bold text-3xl tracking-tight mt-1">The complete guide to CA OS</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            A practical manual for chartered accountants and their teams — with real screenshots. Every module in the app is covered below. Use the table of contents on the left to jump around.
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            <Badge variant="secondary" className="text-[10px]">Last updated: Feb 2026</Badge>
            <Badge variant="secondary" className="text-[10px]">{SECTIONS.length} modules</Badge>
            <Badge variant="outline" className="text-[10px] border-primary text-primary">Azure Prism theme</Badge>
          </div>
        </header>

        {filtered.map((s) => (
          <section id={`sec-${s.id}`} key={s.id} className="space-y-6 scroll-mt-6" data-testid={`help-section-${s.id}`}>
            <div>
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <s.icon size={16} />
                </div>
                <h2 className="font-heading font-semibold text-xl tracking-tight">{s.title}</h2>
              </div>
              <p className="text-sm text-muted-foreground mt-2 ml-12">{s.intro}</p>
            </div>

            <div className="space-y-6 ml-0">
              {s.steps.map((st, i) => (
                <Card key={st.title} className="p-0 overflow-hidden border-border shadow-none" data-testid={`help-step-${s.id}-${i}`}>
                  <div className="border-b border-border bg-muted/40 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Step {i + 1}</div>
                    <div className="font-heading font-semibold text-sm mt-0.5">{st.title}</div>
                  </div>
                  <div className="p-4 space-y-4">
                    <p className="text-sm text-foreground/90 leading-relaxed">{st.body}</p>
                    <div className="border border-border rounded-lg overflow-hidden bg-background">
                      <img
                        src={`/manual/${st.img}`}
                        alt={st.title}
                        loading="lazy"
                        className="w-full block"
                        data-testid={`help-img-${s.id}-${i}`}
                      />
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {s.tips && s.tips.length > 0 && (
              <Card className="p-4 border-primary/30 bg-primary/5 shadow-none">
                <div className="text-[10px] uppercase tracking-widest text-primary font-semibold mb-2">Pro tips</div>
                <ul className="space-y-1.5">
                  {s.tips.map((tip, i) => (
                    <li key={`${s.slug || s.title || "tip"}-${i}`} className="text-sm text-foreground/90 flex gap-2">
                      <span className="text-primary font-mono">›</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </section>
        ))}

        <footer className="border-t border-border pt-6 pb-12 text-xs text-muted-foreground">
          Need something not covered here? Ping your workspace admin or open a support ticket. Built with ♥ by the CA OS team.
        </footer>
      </main>
    </div>
  );
}
